import { getWsToken, getWsUrl, Tier } from './api';

export const CloseCode = {
  PREVIEW_EXPIRED: 4001,
  TRIAL_EXPIRED: 4002,
  PAYWALL: 4003,
  UNAUTHORIZED: 4004,
  SERVER_ERROR: 4005,
} as const;

export interface MetaMessage {
  type: 'meta';
  tier: Tier;
  expires_at: string | null;
  server_now: string;
}

export interface TickMessage {
  type: 'tick';
  tier: Tier;
  data: Record<string, unknown>;
}

export type WsMessage = MetaMessage | TickMessage;

export interface WsCallbacks {
  onMeta: (msg: MetaMessage) => void;
  onTick: (data: Record<string, unknown>) => void;
  onClose: (code: number, reason: string) => void;
  onConnected: () => void;
  onDisconnected: () => void;
}

const TOKEN_REFRESH_INTERVAL = 50000; // 50 seconds
const RECONNECT_DELAYS = [500, 1000, 2000, 4000, 8000];

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private callbacks: WsCallbacks;
  private token: string | null = null;
  private tokenRefreshTimer: number | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private shouldReconnect = true;

  constructor(callbacks: WsCallbacks) {
    this.callbacks = callbacks;
  }

  async connect(): Promise<boolean> {
    this.shouldReconnect = true;
    this.reconnectAttempt = 0;

    const tokenResponse = await getWsToken();
    if (!tokenResponse) {
      return false;
    }

    this.token = tokenResponse.token;
    this.establishConnection();
    this.startTokenRefresh();
    return true;
  }

  private establishConnection(): void {
    if (!this.token) return;

    const wsUrl = `${getWsUrl()}/ws?token=${this.token}`;
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.callbacks.onConnected();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);

        if (msg.type === 'meta') {
          this.callbacks.onMeta(msg);
        } else if (msg.type === 'tick') {
          this.callbacks.onTick(msg.data);
        }
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    this.ws.onclose = (event) => {
      this.callbacks.onDisconnected();
      this.handleClose(event.code, event.reason);
    };

    this.ws.onerror = () => {
      // Error will be followed by close event
    };
  }

  private handleClose(code: number, reason: string): void {
    this.callbacks.onClose(code, reason);

    // Don't reconnect for access-related closures
    if (
      code === CloseCode.PREVIEW_EXPIRED ||
      code === CloseCode.TRIAL_EXPIRED ||
      code === CloseCode.PAYWALL ||
      code === CloseCode.UNAUTHORIZED
    ) {
      this.stopTokenRefresh();
      return;
    }

    // Reconnect for other closures (network issues, server errors)
    if (this.shouldReconnect) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
    this.reconnectAttempt++;

    this.reconnectTimer = window.setTimeout(async () => {
      const tokenResponse = await getWsToken();
      if (tokenResponse) {
        this.token = tokenResponse.token;
        this.establishConnection();
      } else {
        // No valid session, stop reconnecting
        this.callbacks.onClose(CloseCode.UNAUTHORIZED, 'Session expired');
      }
    }, delay);
  }

  private startTokenRefresh(): void {
    this.stopTokenRefresh();

    this.tokenRefreshTimer = window.setInterval(async () => {
      const tokenResponse = await getWsToken();
      if (tokenResponse) {
        this.token = tokenResponse.token;
        // If connection is still open, we're good - token ready for reconnect
        // If connection dropped, it will use the fresh token
      }
    }, TOKEN_REFRESH_INTERVAL);
  }

  private stopTokenRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopTokenRefresh();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
